import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';
import { ClinicTextInput } from '@/components/ClinicTextInput';

type InsuranceForm = {
  insuranceCompany: string;
  enrolleeId: string;
  groupId: string;
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

export function InsuranceInfoPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();

  const [form, setForm] = useState<InsuranceForm>({
    insuranceCompany: '',
    enrolleeId: '',
    groupId: '',
  });
  const [errors, setErrors] = useState<Partial<InsuranceForm>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(
        new RegExp(
          '(?:^|; )' +
            name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
            '=([^;]*)'
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    };

    setForm({
      insuranceCompany: getCookie('insurance_company') || '',
      enrolleeId: getCookie('insurance_enrollee_id') || '',
      groupId: getCookie('insurance_group_id') || '',
    });
  }, []);

  const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=604800`;
  };

  const updateField = (key: keyof InsuranceForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    if (key === 'insuranceCompany') setCookie('insurance_company', value);
    if (key === 'enrolleeId') setCookie('insurance_enrollee_id', value);
    if (key === 'groupId') setCookie('insurance_group_id', value);
  };

  const isValid = () => {
    const newErrors: Partial<InsuranceForm> = {};
    if (!form.insuranceCompany.trim())
      newErrors.insuranceCompany = 'Insurance company is required';
    if (!form.enrolleeId.trim())
      newErrors.enrolleeId = 'Enrollee ID is required';
    if (!form.groupId.trim()) newErrors.groupId = 'Group ID is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!clinicSlug) return;
    if (!isValid()) return;

    try {
      setIsSaving(true);
      setCookie('insurance_company', form.insuranceCompany);
      setCookie('insurance_enrollee_id', form.enrolleeId);
      setCookie('insurance_group_id', form.groupId);
      navigate(`/${clinicSlug}/request-submitted`);
    } finally {
      setIsSaving(false);
    }
  };

  if (clinicLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
    >
      <motion.div className='flex-shrink-0 p-6' variants={fadeInUp}>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full'>
          <motion.div className='text-left mb-12 mt-8' variants={fadeInUp}>
            <h2
              className='text-2xl lg:text-6xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              AI Will Help You to Get Best of Your Insurance
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.div className='space-y-6' variants={fadeInUp}>
              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Insurance Company'
                  value={form.insuranceCompany}
                  onChange={v => updateField('insuranceCompany', v)}
                  placeholder='insurance company name'
                  error={errors.insuranceCompany as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Enrollee ID'
                  value={form.enrolleeId}
                  onChange={v => updateField('enrolleeId', v)}
                  placeholder='your enrollee ID'
                  error={errors.enrolleeId as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Group ID'
                  value={form.groupId}
                  onChange={v => updateField('groupId', v)}
                  placeholder='your group ID'
                  error={errors.groupId as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div className='flex justify-start mt-12' variants={scaleIn}>
            <motion.button
              disabled={isSaving}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5  font-semibold hover:opacity-90 ${
                isSaving ? 'opacity-80 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Processing...
                </>
              ) : (
                'Submit'
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      <motion.div variants={fadeInUp}>
        <Footer mobileColumnLayout={true} />
      </motion.div>
    </motion.div>
  );
}

export default InsuranceInfoPage;
